import React from 'react';
import { Box, useTheme } from '@mui/material';
import type { DefaultTheme } from '@mui/styles';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/pro-light-svg-icons';
import {
  faBracketsCurly,
  faShieldXmark,
  faFilter,
  faXmarkToSlot,
  faFileInvoiceDollar,
  faClockRotateLeft,
  faArrowRightArrowLeft,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faListTree,
  faFiles,
  faLayerGroup,
} from '@fortawesome/pro-solid-svg-icons';
import { formatCurrency } from '@cowbell/ui/core';
import { BaseFlag } from './FlagCell';
import { ProductTypes } from '../../console/_statics/product.statics.json';

// Canonical row.flags[] entry served by the backend (FIR-20828 protocol).
// metadata is an opaque kvp bag — readers cast individual keys as
// needed. The backend decides which flags to emit; the frontend just
// renders what's there.
type FlagEntry = {
  type: string;
  metadata?: Record<string, unknown>;
};

export type FlagRow = {
  flags?: ReadonlyArray<FlagEntry>;
  /** ISO 4217 code from the API. Row-level, so it is passed to tooltips rather than
   * duplicated into every flag's metadata. */
  currency?: string;
};

type FlagConfig = {
  icon: IconDefinition;
  color: (theme: DefaultTheme, flag: FlagEntry) => string;
  iconColor?: (theme: DefaultTheme) => string;
  // Most flags describe themselves from their own metadata; a few need a row-level
  // field (currency) that does not belong in a per-flag payload.
  tooltip: (flag: FlagEntry, row: FlagRow) => React.ReactNode;
};

const metaString = (flag: FlagEntry, key: string) =>
  typeof flag.metadata?.[key] === 'string'
    ? (flag.metadata[key] as string)
    : undefined;

const metaNumber = (flag: FlagEntry, key: string) =>
  typeof flag.metadata?.[key] === 'number'
    ? (flag.metadata[key] as number)
    : undefined;

// Visual config per flag type. Adding a new flag = one entry here
// (plus a palette key on the theme). Type strings match the backend's
// FlagType enum (fir-account com.cowbell.server.accountmodules.common.flags.FlagType).
const flagConfig: Record<string, FlagConfig> = {
  CLAIM: {
    icon: faCircleExclamation,
    color: (t, f) =>
      metaString(f, 'claimStatus') !== 'OPEN'
        ? t.palette.flags.closedClaim
        : t.palette.flags.openClaim,
    tooltip: (f) => {
      const status = metaString(f, 'claimStatus') ?? '';
      const number = metaString(f, 'claimNumber');
      const text = number
        ? `${status} - Claim Number ${number}`
        : `${status.toLowerCase()} Claim`;
      return <Box sx={{ textTransform: 'capitalize' }}>{text}</Box>;
    },
  },
  API: {
    icon: faBracketsCurly,
    color: (t) => t.palette.flags.api,
    tooltip: () => 'Is API',
  },
  PROGRAM: {
    icon: faListTree,
    color: (t) => t.palette.flags.program,
    tooltip: (f) => `Program: ${metaString(f, 'programName') ?? ''}`,
  },
  POOL: {
    icon: faListTree,
    color: (t) => t.palette.flags.program,
    tooltip: (f) => `Pool: ${metaString(f, 'poolName') ?? ''}`,
  },
  RATER_X: {
    icon: faShieldXmark,
    color: (t) => t.palette.flags.raterX,
    iconColor: (t) => t.palette.flags.raterXIcon,
    tooltip: () => 'Rater X Compatible',
  },
  ENDORSEMENTS: {
    icon: faFiles,
    color: (t) => t.palette.flags.endorsements,
    iconColor: (t) => t.palette.flags.raterXIcon,
    tooltip: (f) => `${metaNumber(f, 'endorsementCount') ?? 0} endorsements`,
  },
  UNVERIFIED: {
    icon: faXmarkToSlot,
    color: (t) => t.palette.flags.unverified,
    tooltip: () => 'Unverified',
  },
  SHORT_FORM: {
    icon: faFilter,
    color: (t) => t.palette.flags.shortForm,
    iconColor: (t) => t.palette.flags.raterXIcon,
    tooltip: () => 'Short Form',
  },
  ACTIVE_PAYMENT_EXTENSION: {
    icon: faFileInvoiceDollar,
    color: (t) => t.palette.flags.activePaymentExtension,
    tooltip: () => 'Payment Due Date Extended',
  },
  TAIL: {
    icon: faClockRotateLeft,
    color: (t) => t.palette.flags.tail,
    tooltip: () => 'Tail Coverage',
  },
  EXCESS_TOWER: {
    icon: faLayerGroup,
    color: (t) => t.palette.flags.excessTower,
    iconColor: (t) => t.palette.flags.raterXIcon,
    tooltip: (f, row) => excessTowerTooltip(f, row),
  },
  PRODUCT_CHANGE: {
    icon: faArrowRightArrowLeft,
    color: (t) => t.palette.flags.productChange,
    tooltip: (f) =>
      `Migrated from ${productName(
        metaString(f, 'fromProduct')
      )} to ${productName(metaString(f, 'toProduct'))}`,
  },
};

const productName = (key?: string) =>
  ProductTypes[key?.toUpperCase() as keyof typeof ProductTypes]?.full ??
  key ??
  '';

/**
 * "Excess tower — 3 layers below, attaches at $5,000,000".
 *
 * A plain string, not a node: MUI derives the tooltip's aria-label from a string title, which is
 * both how these flags are reachable to screen readers and how they are asserted in tests.
 *
 * The attachment clause is dropped outright when the row carries no currency. Rendering "attaches
 * at -" would be worse than saying nothing, and a guessed symbol misstates money.
 */
const excessTowerTooltip = (flag: FlagEntry, row: FlagRow) => {
  const layersBelow = metaNumber(flag, 'towerLayersBelow');
  if (layersBelow === undefined) {
    return 'Excess tower';
  }
  const layers = `${layersBelow} ${
    layersBelow === 1 ? 'layer' : 'layers'
  } below`;
  const attachment = formatCurrency(
    metaNumber(flag, 'towerAttachment'),
    row.currency,
    { maximumFractionDigits: 0 }
  );
  return attachment
    ? `Excess tower — ${layers}, attaches at ${attachment}`
    : `Excess tower — ${layers}`;
};

const RegistryFlag = ({
  flag,
  row,
}: Readonly<{ flag: FlagEntry; row: FlagRow }>) => {
  const theme = useTheme<DefaultTheme>();
  const cfg = flagConfig[flag.type];
  if (!cfg) return null;
  return (
    <BaseFlag color={cfg.color(theme, flag)} tooltip={cfg.tooltip(flag, row)}>
      <FontAwesomeIcon
        icon={cfg.icon}
        style={cfg.iconColor ? { color: cfg.iconColor(theme) } : undefined}
      />
    </BaseFlag>
  );
};

// Renders every flag the backend emitted. No filtering, no show predicate —
// if it's in row.flags[], it surfaces.
export const renderFlags = (row: FlagRow) =>
  row.flags?.map((f) => <RegistryFlag key={f.type} flag={f} row={row} />);
